import React, { Fragment, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Radio,
  TextField,
  Tooltip,
  IconButton,
  CircularProgress
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'

export type ConfigDataType = 'bool' | 'number' | 'text'

export interface ConfigItem {
  /** Clave única de la fila (nombre del parámetro / tipo de proceso) */
  key: string
  /** Texto visible de la configuración */
  name: string
  /** Descripción usada como tooltip / caption */
  description?: string
  value: string
  dataType: ConfigDataType
  placeholder?: string
}

interface ConfigLineProps extends ConfigItem {
  /**
   * Guarda el nuevo valor. Devuelve true si el guardado fue exitoso
   * (para que la fila salga del modo edición) o false si falló.
   */
  onSave: (newValue: string) => Promise<boolean>
}

export const ConfigLine = ({
  name,
  description,
  value,
  dataType,
  placeholder,
  onSave
}: ConfigLineProps) => {
  // ── Bool: radios Activo/Inactivo, guarda al instante ────────────────────────
  if (dataType === 'bool') {
    return <BoolLine name={name} description={description} value={value} onSave={onSave} />
  }

  // ── Valor / URL: edición explícita por fila (editar → guardar/cancelar) ──────
  return (
    <ValueLine
      name={name}
      description={description}
      value={value}
      dataType={dataType}
      placeholder={placeholder}
      onSave={onSave}
    />
  )
}

const BoolLine = ({
  name,
  description,
  value,
  onSave
}: Pick<ConfigLineProps, 'name' | 'description' | 'value' | 'onSave'>) => {
  const [saving, setSaving] = useState(false)

  const handleChange = async (newValue: string) => {
    if (newValue === value) return
    setSaving(true)
    await onSave(newValue)
    setSaving(false)
  }

  const makeRadio = (radioValue: string) => (
    <Radio
      size='small'
      checked={value === radioValue}
      onChange={() => handleChange(radioValue)}
      disabled={saving}
      sx={{
        color: 'grey.400',
        '&.Mui-checked': { color: 'success.main' }
      }}
    />
  )

  return (
    <Box
      display='grid'
      gridTemplateColumns='1fr minmax(250px, 30%) minmax(250px, 30%)'
      alignItems='center'
      px={3}
      py={1.5}
      sx={{ transition: 'background-color 0.2s' }}
    >
      <Box sx={{ minWidth: 0, overflowWrap: 'anywhere', pr: 2 }}>
        <Typography variant='body2' fontWeight={500}>
          {name}
        </Typography>
        {description && (
          <Typography variant='caption' color='text.secondary'>
            {description}
          </Typography>
        )}
      </Box>

      <Box display='flex' justifyContent='center'>
        {saving ? <CircularProgress size={16} /> : makeRadio('true')}
      </Box>

      <Box display='flex' justifyContent='center'>
        {!saving && makeRadio('false')}
      </Box>
    </Box>
  )
}

const ValueLine = ({
  name,
  description,
  value,
  dataType,
  placeholder,
  onSave
}: Omit<ConfigLineProps, 'key'>) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)

  // Sincroniza el borrador con el valor real mientras no se esté editando
  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const isDirty = editing && draft !== value

  const handleSave = async () => {
    setSaving(true)
    const ok = await onSave(draft)
    setSaving(false)
    if (ok) setEditing(false)
  }

  const handleCancel = () => {
    setDraft(value)
    setEditing(false)
  }

  return (
    <Fragment>
      <Box
        display='grid'
        gridTemplateColumns='1fr minmax(500px, 60%)'
        alignItems='center'
        px={3}
        py={1.5}
        sx={{
          bgcolor: isDirty ? '#fff3e0' : 'transparent',
          transition: 'background-color 0.2s'
        }}
      >
        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere', pr: 2 }}>
          <Typography variant='body2' fontWeight={500}>
            {name}
          </Typography>
          {description && (
            <Typography variant='caption' color='text.secondary'>
              {description}
            </Typography>
          )}
        </Box>

        <Box display='flex' justifyContent='center' alignItems='center' gap={0.5}>
          <TextField
            size='small'
            variant='outlined'
            type={dataType === 'number' ? 'number' : 'text'}
            value={editing ? draft : value}
            placeholder={placeholder}
            disabled={!editing || saving}
            onChange={(e) => setDraft(e.target.value)}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
          />

          {editing ? (
            <Fragment>
              <Tooltip title='Guardar' arrow>
                <span>
                  <IconButton size='small' color='primary' onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize='small' />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Cancelar' arrow>
                <span>
                  <IconButton size='small' onClick={handleCancel} disabled={saving}>
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Fragment>
          ) : (
            <Tooltip title='Editar' arrow>
              <IconButton size='small' onClick={() => setEditing(true)}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {isDirty && (
        <Box px={3} pb={1}>
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeeba',
              color: '#856404',
              borderRadius: '8px',
              padding: '6px 10px'
            }}
          >
            <b>Advertencia:</b> la modificación de parámetros podría generar
            cambios en el sistema.
          </Typography>
        </Box>
      )}
    </Fragment>
  )
}
